
import React, { useEffect, useState, useMemo } from "react";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import notifyImg_One from "../../assets/images/logo avatar.png";
import { TiTick } from "react-icons/ti";
import { ImCross } from "react-icons/im";
import {
  Search,
  Table2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Clock,
} from "lucide-react";
import { FaEdit, FaTrash, FaInfoCircle } from "react-icons/fa";
import { MdPending, MdCheckCircle, MdCancel } from "react-icons/md";
// import "../../assets/css/Notification.css";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

const Notification = () => {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [addData, setAddData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [actionType, setActionType] = useState(""); // "approve" or "reject"

  // GET Data
  const fetchAddData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/notification`, {
        params: {
          table_name: "expenses",
          status: "pending",
        },
      });
      console.log("notification response data", response.data);
      setAddData(response.data || []);
    } catch (error) {
      console.log("Notification data error:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // DELETE Data
  const handleDeleteData = async (id) => {
    try {
      await axios.delete(`${Apiurl}/notification/${id}`);
      console.log("Deleted Notification Data:", id);
      setAddData((prev) => prev.filter((item) => item._id !== id));
      showNotification("Notification deleted successfully");
    } catch (error) {
      console.error("Notification Data Not Deleted", error.message);
      showNotification("Failed to delete notification", true);
    }
  };

  // Handle approval action
  const handleApproveReject = async (notificationId, action) => {
    try {
      const response = await axios.post(
        `${Apiurl}/expense/approval/${notificationId}`,
        {
          action: action,
        }
      );

      if (response.status === 200) {
        showNotification(`Request ${action}d successfully`);
        // Remove the notification from the list
        setAddData((prev) =>
          prev.filter((item) => item._id !== notificationId)
        );

        // If it's an expense approval, refresh the expenses list if needed
        if (action === "approve") {
          // You might want to trigger a refresh of the expenses list here
        }
      }
    } catch (error) {
      console.error(`Error ${action}ing request:`, error.message);
      showNotification(`Failed to ${action} request`, true);
    } finally {
      closeActionModal();
    }
  };

  // Open action modal
  const openActionModal = (notification, action) => {
    setSelectedNotification(notification);
    setActionType(action);
    setShowActionModal(true);
  };

  // Close action modal
  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedNotification(null);
    setActionType("");
  };

  useEffect(() => {
    fetchAddData();
  }, []);

  // Function to show notification
  const showNotification = (msg, error = false) => {
    setMessage(msg);
    setIsError(error);
    setShow(true);
    setTimeout(() => setShow(false), 5000);
  };

  // Filter data
  const filteredData = useMemo(() => {
    const result = addData.filter((item) =>
      [
        item.table_name,
        item.table_id,
        item.employee_id,
        item.employee_name,
        item.action_type,
        item.status,
      ].some((prop) =>
        prop != null
          ? prop.toString().toLowerCase().includes(searchTerm.toLowerCase())
          : false
      )
    );
    return result;
  }, [addData, searchTerm]);

  // Update items per page
  useEffect(() => {
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Reset to first page when search term or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  // Delete modal functions
  const openDeleteModal = (id) => {
    if (!id) {
      console.warn("Invalid notification ID:", id);
      return;
    }
    console.log("Opening Delete Notification modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    console.log("Closing Delete Notification modal");
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!deleteId) {
      console.warn("Invalid notification ID:", deleteId);
      return;
    }
    try {
      console.log("Deleting notification with ID:", deleteId);
      await handleDeleteData(deleteId);
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting notification:",
        error.message,
        error.response?.data
      );
    }
  };

  // View expense details
  const viewExpenseDetails = async (tableId) => {
    try {
      const response = await axios.get(`${Apiurl}/expense/${tableId}`);
      const expense = response.data;

      // Show details in a modal or alert
      alert(`Expense Details:\n
Date: ${expense.date ? new Date(expense.date).toLocaleDateString() : "N/A"}\n
Department: ${expense.department || "N/A"}\n
Type: ${expense.type || "N/A"}\n
Description: ${expense.description || "N/A"}\n
Amount: ₹${expense.amount || "0"}\n
Status: ${expense.status || "N/A"}\n
Added By: ${expense.addedBy || "N/A"}`);
    } catch (error) {
      console.error("Error fetching expense details:", error);
      showNotification("Failed to fetch expense details", true);
    }
  };

  return (
    <>
      {/* Notification Toast */}
      <ToastContainer
        position="top-end"
        className="text-center w-auto"
        style={{ marginTop: "65px", marginRight: "40px", zIndex: 1 }}
      >
        <Toast
          animation={false}
          className={`slide-in`}
          bg={isError ? "danger" : "success"}
          onClose={() => setShow(false)}
          show={show}
          delay={5000}
          autohide
        >
          <Toast.Header>
            <img
              src={notifyImg_One}
              style={{ width: "30px", height: "30px" }}
              className="rounded me-2"
              alt="logo"
            />
            <strong className="me-auto">Tamil</strong>
            <small className="text-muted">just now</small>
          </Toast.Header>
          <Toast.Body className="text-white">{message}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Table */}
      <div className="p-2">
        <div className="bg-white rounded p-3">
          {/* Top Controls */}
          <div className="row mb-3 crm-table-toolbar">
            {/* Rows Per Page */}
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <Table2 size={16} />
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => setShowEntries(e.target.value)}
                >
                  <option value="5">Show 5</option>
                  <option value="10">Show 10</option>
                  <option value="20">Show 20</option>
                  <option value="50">Show 50</option>
                  <option value="100">Show 100</option>
                  <option value="200">Show 200</option>
                  <option value="500">Show 500</option>
                  <option value="1000">Show 1000</option>
                  <option value="All">Show All</option>
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            {/* Search */}
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  {searchTerm && (
                    <button
                      className="position-absolute bg-transparent border-0"
                      style={{
                        right: "30px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <ImCross className="text-muted" size={12} />
                    </button>
                  )}
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "32%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr className="text-center">
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    S.No
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Table Name
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Table ID
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Employee Name
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Employee ID
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Action Type
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Status
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Date
                  </th>
                  <th style={{ backgroundColor: "#E7F8FA", color: "#018A97", fontWeight: 600 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((data, index) => (
                    <tr key={data._id || index} className="text-center">
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{data.table_name}</td>
                      <td>
                        <button
                          className="btn btn-link p-0"
                          onClick={() => viewExpenseDetails(data.table_id)}
                          title="View Expense Details"
                        >
                          {data.table_id}
                        </button>
                      </td>
                      <td>{data.employee_name}</td>
                      <td>{data.employee_id}</td>
                      <td>
                        <span
                          className={`badge d-flex align-items-center justify-content-center gap-1 p-2 ${
                            data.action_type === "edit"
                              ? "bg-warning"
                              : data.action_type === "delete"
                              ? "bg-danger"
                              : "bg-info"
                          }`}
                        >
                          {data.action_type === "edit" && <FaEdit />}
                          {data.action_type === "delete" && <FaTrash />}
                          {data.action_type !== "edit" &&
                            data.action_type !== "delete" && <FaInfoCircle />}
                          {data.action_type}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`badge d-flex align-items-center justify-content-center gap-1 p-2 text-center ${
                            data.status === "pending"
                              ? "bg-warning"
                              : data.status === "approved"
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {data.status === "pending" && <MdPending />}
                          {data.status === "approved" && <MdCheckCircle />}
                          {data.status === "rejected" && <MdCancel />}
                          {data.status}
                        </span>
                      </td>
                      <td>
                        {data.time
                          ? new Date(data.time).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm text-success"
                          onClick={() => openActionModal(data, "approve")}
                          title="Approve"
                        >
                          <TiTick size={22} />
                        </button>
                        <button
                          className="btn btn-sm text-danger"
                          onClick={() => openActionModal(data, "reject")}
                          title="Reject"
                        >
                          <ImCross size={15} />
                        </button>
                        <button
                          className="btn btn-sm text-secondary"
                          title="View Details"
                          onClick={() => viewExpenseDetails(data.table_id)}
                        >
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      <div className="text-muted">
                        <Search size={24} className="mb-2" />
                        <br />
                        No pending notification records found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredData.length > 0 && showEntries !== "All" && (
            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage > 1 && handlePageClick(currentPage - 1)
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                  <span className="d-none d-sm-inline">Previous</span>
                </button>

                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === currentPage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === currentPage
                          ? { backgroundColor: "#01AEBE", border: "none" }
                          : {}
                      }
                      onClick={() => handlePageClick(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    currentPage < totalPages && handlePageClick(currentPage + 1)
                  }
                  disabled={currentPage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Confirmation Modal */}
      {showActionModal && selectedNotification && (
        <div
          className={`modal fade ${showActionModal ? "show" : ""}`}
          style={{ display: showActionModal ? "block" : "none" }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="actionModalLabel"
          aria-hidden={!showActionModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeActionModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor:
                        actionType === "approve" ? "#d1fae5" : "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    {actionType === "approve" ? (
                      <TiTick size={20} style={{ color: "#059669" }} />
                    ) : (
                      <ImCross size={12} style={{ color: "#dc2626" }} />
                    )}
                  </span>
                </div>
                <h5 className="modal-title" id="actionModalLabel">
                  {actionType === "approve" ? "Approve" : "Reject"} Request
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to {actionType} this{" "}
                  {selectedNotification.action_type} request?
                </p>
                <div className="mt-3 text-start">
                  <p>
                    <strong>Employee:</strong>{" "}
                    {selectedNotification.employee_name}
                  </p>
                  <p>
                    <strong>Action:</strong> {selectedNotification.action_type}
                  </p>
                  <p>
                    <strong>Table ID:</strong> {selectedNotification.table_id}
                  </p>
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeActionModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={
                    actionType === "approve"
                      ? "btn btn-success btn-sm"
                      : "btn btn-danger btn-sm"
                  }
                  onClick={() =>
                    handleApproveReject(selectedNotification._id, actionType)
                  }
                >
                  Yes, {actionType === "approve" ? "Approve" : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="deleteNotificationModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <ImCross size={12} style={{ color: "#dc2626" }} />
                  </span>
                </div>
                <h5 className="modal-title" id="deleteNotificationModalLabel">
                  Delete Notification
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this notification?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                >
                  Yes, Delete It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showActionModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default Notification;